import { Container, HStack, Heading, Stack, Text } from '@chakra-ui/react'
import { ICourse } from '@/shared/ICourse'
import React from 'react';
import { useParams } from 'react-router-dom';
import { CourseStep } from './CourseStep';
import { CourseStorageService } from '@/app/services/CourseStorageService';
import { TelemetryClientService } from '@/app/services/TelemetryClientService';
import { CourseModuleStorageService } from '@/app/services/CourseModuleStorageService';
import { ICourseModule } from '@/shared/ICourseModule';
import { AppContext } from '../comments/AppContext';
import { WorkflowService } from '@/shared/WorkflowService';
import { AuthService } from '../services/AuthService';
import { ButtonRefresh } from './ButtonRefresh';
import { LabelAwarded } from './LabelAwarded';
import { ArchivedBadge } from '../components/ArchivedBadge';
import { ErrorMessage } from '../components/ErrorMessage';
import { CoursePageMenu } from '../review/CoursePageMenu';
import { EmailLink } from '../components/EmailLink';
import { AppBreadcrumb } from '../navbar/AppBreadcrumb';

export const CoursePage = () => {

  const params = useParams<{ courseId: string }>();

  TelemetryClientService.page('CoursePage');

  const [course, setCourse] = React.useState<ICourse | undefined>();
  const [modules, setModules] = React.useState<ICourseModule[] | undefined>();
  const [errorMessage, setErrorMessage] = React.useState<any>();

  const [isLoading, setIsLoading] = React.useState(false);

  const { isAuthenticated, setStatus } = React.useContext(AppContext);

  const loadCourse = async (courseId: string, force?: boolean) => {
    try {
      setIsLoading(true);
      setStatus('Loading...');
      setErrorMessage(undefined);

      const loaded = await CourseStorageService.getCourse(courseId, force);
      setCourse(loaded);

      const modules = await CourseModuleStorageService.getCourseModules(courseId, force);
      setModules(modules);

    } catch (err) {
      setCourse(undefined);
      setErrorMessage(err);
    } finally {
      setStatus(undefined);
      setIsLoading(false);
    }
  }

  React.useEffect(() => {
    if (params.courseId) {
      loadCourse(params.courseId);
    }
  }, [params.courseId, isAuthenticated]);

  const onRefresh = () => {
    if (params.courseId) {
      loadCourse(params.courseId, true);
    }
  }

  const breadcrumb = React.useMemo(() => [
    { href: '/courses', label: 'Courses' },
    { href: `/course/${course?.id}`, label: course?.folderName ?? "Loading..." },
  ], [course]);

  const roles = AuthService.getCourseRoles(course);
  const hint = course && WorkflowService.getDetailedStepDescription(course, roles);

  return <>
    <AppBreadcrumb breadcrumb={breadcrumb} />
    <Container mt={100} minH='50vh' py={{ base: '4', md: '8' }} px={{ base: 2, md: 8 }}>
      <HStack spacing='4' justify='space-between'>
        <HStack>
          <Heading size="sm" py='1'>
            <Text as='span' marginRight='3'>{course?.CourseTitle ?? "Loading Course..."}</Text>
            <LabelAwarded award={course?.LabelAwarded} />
            <ArchivedBadge archived={course?.archived} /></Heading>
        </HStack>
        <HStack>
          <ButtonRefresh isDisabled={isLoading} onClick={onRefresh} />
          <CoursePageMenu course={course} setCourse={setCourse} isDisabled={isLoading} />
        </HStack>
      </HStack>
      <HStack color='gray' spacing='6' mb='6'>
        <Text fontWeight='600' size='sm'>{course?.provider?.ProviderName}</Text>
        <EmailLink {...WorkflowService.getPrimaryContact(course)} />
      </HStack>
      {AuthService.isStaff && course?.CourseNotice && (
        <Text 
          fontWeight={600} 
          mb='4' 
          bg='blue.50' 
          border='1px solid' 
          borderColor='blue.200' 
          py='2' 
          px='5' 
          rounded='lg'
          fontSize='sm'
        >
          <Text as='span' fontWeight='bold' color='blue.700'>Staff Notice:</Text>{' '}
          {course.CourseNotice}
        </Text>
      )}
      {hint && <Text fontWeight={600} mb='4' bg={`${hint.color}.50`} border='1px solid' borderColor={`${hint.color}.300`} py='2' px='5' rounded='lg'>
        <span dangerouslySetInnerHTML={{ __html: hint.text }}></span>
      </Text>}
      <ErrorMessage error={errorMessage} />
      {course &&
        <Stack w='100%' spacing="0" pt='14'>
          {WorkflowService.getCourseSteps(course).map(step => (
            <CourseStep
              key={step}
              moduleId={step}
              course={course}
              modules={modules}
              onCourseChanged={setCourse}
            />)
          )}
        </Stack>
      }
    </Container>
  </>
}